import React, { useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import { Card, Header, Loading, Player } from '../components';
import * as ROUTES from '../constants/routes';
import logo from '../logo.svg';
import { SelectProfileContainer } from './profiles';
import { FooterContainer } from './footer';
import { API_URL, getSelectedProfileId } from '../utils';

export function BrowseContainer({ videos, loading: apiLoading, error: apiError }) {
  const [category, setCategory] = useState('films'); // Changed from 'series' to 'films'
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm] = useState('');
  const [slideRows, setSlideRows] = useState([]);
  const [customizedVideos, setCustomizedVideos] = useState([]);

  const user = JSON.parse(localStorage.getItem('authUser')) || {};

  // Restore profile from localStorage on mount
  useEffect(() => {
    const selectedProfileId = localStorage.getItem('selectedProfileId');
    if (selectedProfileId && user.profiles) {
      const savedProfile = user.profiles.find((p) => p.id === selectedProfileId);
      if (savedProfile) {
        console.log('🔄 Restoring profile from localStorage:', savedProfile.name);
        setProfile(savedProfile);
      }
    }
  }, []); // Run once on mount

  const handleProfileSelect = (selectedProfile) => {
    // Save the selected profile
    setProfile(selectedProfile);

    // Store the selected profile ID in localStorage for API calls
    localStorage.setItem('selectedProfileId', selectedProfile.id);
    console.log('✅ Selected profile ID saved:', selectedProfile.id);

    // Dispatch custom event to trigger refetch
    window.dispatchEvent(new Event('profileChanged'));
  };

  // Fetch customized videos for "Gợi ý cho bạn" section
  useEffect(() => {
    const fetchCustomizedVideos = async () => {
      const profileId = getSelectedProfileId();
      if (!profileId) {
        console.log('⚠️ No profile selected, skipping customized videos fetch');
        return;
      }

      try {
        console.log('🎯 Fetching customized videos for profile:', profileId);
        const response = await fetch(`${API_URL}/api/videos/customize?profile_id=${profileId}`, {
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch customized videos');
        }

        const data = await response.json();
        console.log('✅ Customized videos loaded:', data.length);

        // Transform to match expected format
        const transformedVideos = data.map((video) => ({
          docId: video._id,
          id: video._id,
          slug: video.slug,
          title: video.title,
          description: video.synopsis || '',
          genre: video.theme?.[0]?.toLowerCase() || 'general',
          maturity: video.ageRating?.code || 'G',
          videoUrl: video.videoUrl,
          customize_thumbnail: video.customize_thumbnail,
          releaseDate: video.releaseDate,
          duration: video.duration,
          country: video.country,
          rating: video.rating,
          views: video.views,
          theme: video.theme || [],
          categories: video.categories || [],
          director: video.director,
          cast: video.cast || [],
          highlights: video.highlights || [],
          trailer: video.trailer,
        }));

        setCustomizedVideos(transformedVideos);
      } catch (error) {
        console.error('❌ Error fetching customized videos:', error);
      }
    };

    fetchCustomizedVideos();

    // Listen for profile changes
    const handleProfileChange = () => {
      fetchCustomizedVideos();
    };

    window.addEventListener('profileChanged', handleProfileChange);
    return () => {
      window.removeEventListener('profileChanged', handleProfileChange);
    };
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem('authUser');
    localStorage.removeItem('selectedProfileId');
    window.location.href = ROUTES.SIGN_IN;
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  }, [profile.name]);

  useEffect(() => {
    console.log('🔍 Category:', category);
    console.log('🔍 Videos from API:', videos?.length || 0);

    if (category === 'films') {
      // Transform videos from API to the expected format
      const transformedVideos = (videos || []).map((video) => ({
        docId: video._id,
        id: video._id,
        slug: video.slug,
        title: video.title,
        description: video.synopsis || '',
        genre: video.theme?.[0]?.toLowerCase() || 'general',
        maturity: video.ageRating?.code || 'G',
        videoUrl: video.videoUrl,
        thumbnail: video.thumbnail,
        releaseDate: video.releaseDate,
        duration: video.duration,
        country: video.country,
        rating: video.rating,
        views: video.views,
        theme: video.theme || [],
        categories: video.categories || [],
        director: video.director,
        cast: video.cast || [],
        highlights: video.highlights || [],
        trailer: video.trailer,
      }));

      console.log('🎬 Transformed videos:', transformedVideos.length);

      const sections = [];

      // Add "Gợi ý cho bạn" section at the top if we have customized videos
      if (customizedVideos.length > 0) {
        sections.push({
          title: 'Gợi ý theo sở thích của bạn',
          data: customizedVideos,
        });
      }

      // Add "Tất cả phim" section with videos from API
      sections.push({
        title: 'Tất cả phim',
        data: transformedVideos,
      });

      // Handle search if searchTerm exists
      if (searchTerm.length > 3) {
        const allVideos = [...customizedVideos, ...transformedVideos];
        const fuse = new Fuse(allVideos, {
          keys: ['description', 'title', 'genre'],
        });
        const results = fuse.search(searchTerm).map(({ item }) => item);

        if (results.length > 0) {
          setSlideRows([
            {
              title: 'Kết quả tìm kiếm',
              data: results,
            },
          ]);
        } else {
          setSlideRows(sections);
        }
      } else {
        setSlideRows(sections);
      }
    }
  }, [searchTerm, category, customizedVideos, videos]);

  return profile.name ? (
    <>
      {loading ? <Loading src={profile.photoURL} /> : <Loading.ReleaseBody />}

      <Header bg={false}>
        <Header.Frame>
          <Header.Group>
            <Header.Logo to={ROUTES.HOME} src={logo} alt="Netflix" />
            {/* <Header.TextLink active={category === 'series' ? 'true' : 'false'} onClick={() => setCategory('series')}>
              Dành cho bạn
            </Header.TextLink>
            <Header.TextLink active={category === 'films' ? 'true' : 'false'} onClick={() => setCategory('films')}>
              Tất cả phim
            </Header.TextLink> */}
          </Header.Group>
          <Header.Group>
            {/* <Header.Search searchTerm={searchTerm} setSearchTerm={setSearchTerm} /> */}
            <Header.ButtonLink
              to={ROUTES.DASHBOARD}
              style={{
                backgroundColor: '#097F09',
                marginRight: '12px',
                width: 'auto',
                padding: '8px 16px',
                borderRadius: '120px',
              }}
            >
              Video Highlight Dashboard
            </Header.ButtonLink>
            <Header.Profile>
              <Header.Picture src={profile.photoURL} />
              <Header.Dropdown>
                <Header.Group>
                  <Header.TextLink>Sở thích của {profile.name}</Header.TextLink>
                </Header.Group>

                {profile.preferences && (
                  <>
                    <Header.Group>
                      <Header.TextLink
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          marginBottom: '5px',
                          marginTop: '6px',
                        }}
                      >
                        Thể loại: {profile.preferences.favoriteGenres?.join(', ')}
                      </Header.TextLink>
                    </Header.Group>

                    <Header.Group>
                      <Header.TextLink
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          marginBottom: '5px',
                        }}
                      >
                        Diễn viên: {profile.preferences.favoriteActors?.slice(0, 2).join(', ')}
                      </Header.TextLink>
                    </Header.Group>

                    <Header.Group>
                      <Header.TextLink
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          marginBottom: '5px',
                        }}
                      >
                        Đạo diễn: {profile.preferences.favoriteDirectors?.slice(0, 2).join(', ')}
                      </Header.TextLink>
                    </Header.Group>

                    <Header.Group>
                      <Header.TextLink
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          marginBottom: '5px',
                        }}
                      >
                        Quốc gia: {profile.preferences.favoriteCountries?.join(', ')}
                      </Header.TextLink>
                    </Header.Group>

                    <Header.Group>
                      <Header.TextLink
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          marginBottom: '5px',
                        }}
                      >
                        Độ dài: {profile.preferences.preferredDuration} phút
                      </Header.TextLink>
                    </Header.Group>

                    <Header.Group>
                      <Header.TextLink style={{ fontSize: '11px', color: '#999' }}>
                        Chất lượng: {profile.preferences.streamingQuality}
                      </Header.TextLink>
                    </Header.Group>
                  </>
                )}
              </Header.Dropdown>
            </Header.Profile>
            <Header.TextLink
              onClick={handleSignOut}
              aria-label="Đăng xuất"
              title="Đăng xuất"
              style={{
                marginLeft: '24px',
                marginRight: 0,
                backgroundColor: '#152415',
                padding: '8px',
                borderRadius: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </Header.TextLink>
          </Header.Group>
        </Header.Frame>
      </Header>

      <Header src="banner" dontShowOnSmallViewPort>
        <Header.Feature topPadding="72px">
          {/* <Header.FeatureCallOut>Vieon trên TV</Header.FeatureCallOut>
          <Header.Text>Ngập tràn phim Việt hay nhất mỗi ngày</Header.Text>
          <Header.PlayButton>Xem ngay</Header.PlayButton> */}
        </Header.Feature>
      </Header>

      <Card.Group
        style={{
          paddingTop: '32px',
          paddingBottom: '48px',
        }}
      >
        {apiError && (
          <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>
            <p>
              <span role="img" aria-label="warning">
                ⚠️
              </span>{' '}
              Lỗi khi tải dữ liệu: {apiError}
            </p>
          </div>
        )}

        {apiLoading && (
          <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>
            <p>
              <span role="img" aria-label="loading">
                ⏳
              </span>{' '}
              Đang tải phim...
            </p>
          </div>
        )}

        {!apiLoading &&
          !apiError &&
          slideRows &&
          slideRows.map((slideItem) => {
            const isCustomizeSection = slideItem.title === 'Gợi ý theo sở thích của bạn';

            return (
              <Card key={`${category}-${slideItem.title.toLowerCase()}`}>
                <Card.Title>{slideItem.title}</Card.Title>
                <Card.Entities>
                  {slideItem.data.map((item) => {
                    // Get thumbnail from API: customize_thumbnail for customized videos, thumbnail for all videos
                    const thumbnailUrl =
                      item.customize_thumbnail ||
                      item.thumbnail ||
                      `/images/${category}/${item.genre}/${item.slug}/small.jpg`;

                    return (
                      <Card.Item key={item.docId} item={item}>
                        <Card.Image src={thumbnailUrl} />
                        <Card.Meta style={isCustomizeSection ? { display: 'block' } : {}}>
                          <Card.SubTitle style={isCustomizeSection ? { display: 'block' } : {}}>
                            {item.title}
                          </Card.SubTitle>
                          <Card.Text style={isCustomizeSection ? { display: 'block' } : {}}>
                            {item.description}
                          </Card.Text>
                        </Card.Meta>
                      </Card.Item>
                    );
                  })}
                </Card.Entities>
                <Card.Feature category={category}>
                  <Player>
                    <Player.Button />
                    <Player.Video src="/videos/bunny.mp4" />
                  </Player>
                </Card.Feature>
              </Card>
            );
          })}
      </Card.Group>
      <FooterContainer />
    </>
  ) : (
    <SelectProfileContainer user={user} setProfile={handleProfileSelect} />
  );
}
